import { container } from 'tsyringe';
import { get } from 'lit-translate';
import { CommandName } from '../commands/command-name.enum';
import {
    BOARD_TYPE_DAILY,
    BOARD_TYPE_PROJECT,
    CALENDAR_PROJECT_UID,
    ISSUE_TYPE_PUBLIC_HOLIDAY,
    ISSUE_TYPE_VACATION,
} from '../constants';
import { Employee } from '../domain/entities/employee.entity';
import { Issue } from '../domain/entities/issue.entity';
import { IssueType } from '../domain/entities/issue-type.entity';
import { IssueStatus } from '../domain/entities/issue-status.entity';
import { Keyword } from '../domain/entities/keyword.entity';
import { IssueRepository } from '../domain/repositories/issue.repository';
import { IssueTypeRepository } from '../domain/repositories/issue-type.repository';
import { ProjectRepository } from '../domain/repositories/project.repository';
import { DialogHelper } from '../helpers/dialog.helper';
import { UiHelper } from '../helpers/ui.helper';
import { hasPrivilege } from '../helpers/privilege.helper';
import { EntityManager } from '../persistence/entity.manager';
import { ActionIds } from './action.types';
import type { ActionRegistryImpl } from './action-registry';
import { VACATION_TITLE_PREFIX, vacationTitlePrefix } from '../components/_dialogs/ha-dialog-issue-edit/vacation-auto-title.helper';
import { initialTypeFor } from '../domain/helpers/issue-type-root.helper';
import { loadedIssueTypes } from '../helpers/loaded-issue-types.helper';

/**
 * A new issue in the calendar project, on the type its root starts with (initialTypeFor: the root, its
 * only child, or the abstract root as a placeholder that the dialog makes the user replace). It carries
 * no status: the Absence and Holiday roots are statusless, and the backend drops a status for them. The
 * root is looked up only while the type list is not loaded yet.
 */
async function createCalendarIssueOn(rootUid: string): Promise<Issue> {
    const issue = await container.resolve(IssueRepository).create();
    issue.type = initialTypeFor(rootUid, loadedIssueTypes())
        ?? (await container.resolve(IssueTypeRepository).findOneById(rootUid))!;
    issue.project = await container.resolve(ProjectRepository).findOneById(CALENDAR_PROJECT_UID);
    issue.status = null;
    return issue;
}

/**
 * Registers all dialog-opening actions with the given registry.
 *
 * Dialog actions create new entity instances and open edit dialogs.
 * Authorization is checked synchronously via hasPrivilege() from appState.privileges.
 *
 * @param registry - The ActionRegistryImpl to register actions with.
 */
export function registerDialogActions(registry: ActionRegistryImpl): void {
    registry.register({
        id: ActionIds.DIALOG_NEW_ISSUE,
        label: 'New Issue',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_ISSUE),
        execute: async () => {
            const issue = await container.resolve(IssueRepository).create();
            UiHelper.openDialogEntityEdit(Issue, issue, { creationMode: true, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_PUBLIC_HOLIDAY,
        label: 'New Public Holiday',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_ISSUE),
        execute: async () => {
            const issue = await createCalendarIssueOn(ISSUE_TYPE_PUBLIC_HOLIDAY);
            UiHelper.openDialogEntityEdit(Issue, issue, { creationMode: true, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_VACATION,
        label: 'New Vacation',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_ISSUE),
        execute: async () => {
            const issue = await createCalendarIssueOn(ISSUE_TYPE_VACATION);
            // Anke, 2026-08-19/20: real initial title text, not just a placeholder — it stays in the
            // field (and is submitted as-is) if the user never touches it. "Urlaub" on the Absence root,
            // the child's own name on a child (D55); overwritten with "<prefix> <employee>" once an
            // employee is picked (ha-dialog-issue-edit-orchestrator.component.ts, vacation-auto-title.helper.ts).
            // THIS action — wired to the "Abwesenheit" menu item (o2d-add-entity-menu.toolbar-item.ts) —
            // is the one reached at runtime; the former OpenNewVacationIssueDialogCommand was dead code.
            issue.title = vacationTitlePrefix(issue.type) ?? VACATION_TITLE_PREFIX;
            UiHelper.openDialogEntityEdit(Issue, issue, { creationMode: true, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_PROJECT,
        label: 'New Project',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_PROJECT),
        execute: async () => {
            const project = await container.resolve(ProjectRepository).create(BOARD_TYPE_PROJECT);
            const title = get('ha-boards-view.add-entity.add-project');
            DialogHelper.showDialog('ha-dialog-project-edit', { title, project, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_DAILY,
        label: 'New Daily',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_PROJECT),
        execute: async () => {
            const daily = await container.resolve(ProjectRepository).create(BOARD_TYPE_DAILY);
            const title = get('ha-boards-view.add-entity.add-daily');
            DialogHelper.showDialog('ha-dialog-daily-edit', { title, daily, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_EMPLOYEE,
        label: 'New Employee',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_EMPLOYEE),
        execute: async () => {
            const entityManager = container.resolve(EntityManager);
            const repository = entityManager.getRepositoryForEntity(Employee);
            if (!repository) {
                throw new Error('Employee repository not found');
            }
            const employee = await repository.create();
            UiHelper.openDialogEntityEdit(Employee, employee, { creationMode: true, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_KEYWORD,
        label: 'New Keyword',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_KEYWORD),
        execute: async () => {
            const entityManager = container.resolve(EntityManager);
            const repository = entityManager.getRepositoryForEntity(Keyword);
            if (!repository) {
                throw new Error('Keyword repository not found');
            }
            const keyword = await repository.create();
            UiHelper.openDialogEntityEdit(Keyword, keyword, { creationMode: true, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_ISSUE_TYPE,
        label: 'New Issue Type',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_ISSUE_TYPE),
        execute: async () => {
            const entityManager = container.resolve(EntityManager);
            const repository = entityManager.getRepositoryForEntity(IssueType);
            if (!repository) {
                throw new Error('IssueType repository not found');
            }
            const issueType = await repository.create();
            UiHelper.openDialogEntityEdit(IssueType, issueType, { creationMode: true, closable: true });
        },
    });

    registry.register({
        id: ActionIds.DIALOG_NEW_ISSUE_STATUS,
        label: 'New Issue Status',
        category: 'create',
        canExecute: () => hasPrivilege(CommandName.CREATE_ISSUE_STATUS),
        execute: async () => {
            const entityManager = container.resolve(EntityManager);
            const repository = entityManager.getRepositoryForEntity(IssueStatus);
            if (!repository) {
                throw new Error('IssueStatus repository not found');
            }
            const issueStatus = await repository.create();
            UiHelper.openDialogEntityEdit(IssueStatus, issueStatus, { creationMode: true, closable: true });
        },
    });
}
